/* SmartEvaluate RFP — frontend API client + in-memory file blob holder.
 *
 * Endpoints (see /app/backend/rfp_routes.py):
 *   - POST   /api/rfp/submissions                              (multipart)
 *   - POST   /api/rfp/submissions/{id}/documents               (multipart)
 *   - DELETE /api/rfp/submissions/{id}/documents/{doc_id}
 *   - PATCH  /api/rfp/submissions/{id}                         (JSON)
 *   - GET    /api/rfp/submissions/{id}/documents/{doc_id}/download
 *
 * File blob holder
 * ----------------
 * Browser sessionStorage cannot persist File blobs — only their metadata. To
 * carry the actual uploads from the intake page to the review page, we keep
 * the File objects in a module-level Map. The intake page calls `setFiles()`
 * and the review page reads them on submit. State is wiped on hard reload —
 * that's acceptable (the intake step is just one screen back).
 */
import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/rfp`;
const ax = axios.create({ baseURL: BASE, withCredentials: true });

/* --------------------------- File blob holder ---------------------------- */
const _fileStore = new Map(); // id → File

export function rfpStoreSet(id, file) {
  _fileStore.set(id, file);
}
export function rfpStoreGet(id) {
  return _fileStore.get(id);
}
export function rfpStoreRemove(id) {
  _fileStore.delete(id);
}
export function rfpStoreClear() {
  _fileStore.clear();
}
export function rfpStoreCount() {
  return _fileStore.size;
}

/* --------------------------- API calls ----------------------------------- */
export async function submitRfp({ clientInfo, notes, files }) {
  const form = new FormData();
  form.append("client_info", JSON.stringify(clientInfo || {}));
  form.append("notes", notes || "");
  (files || []).forEach((f) => {
    if (f) form.append("files", f, f.name);
  });
  const r = await ax.post("/submissions", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

export async function addRfpDocuments(assessmentId, files) {
  const form = new FormData();
  (files || []).forEach((f) => {
    if (f) form.append("files", f, f.name);
  });
  const r = await ax.post(`/submissions/${assessmentId}/documents`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

export async function removeRfpDocument(assessmentId, docId) {
  const r = await ax.delete(`/submissions/${assessmentId}/documents/${docId}`);
  return r.data;
}

export async function patchRfpSubmission(assessmentId, body) {
  const r = await ax.patch(`/submissions/${assessmentId}`, body);
  return r.data;
}

export function rfpDocumentDownloadUrl(assessmentId, docId) {
  return `${BASE}/submissions/${assessmentId}/documents/${docId}/download`;
}

/* PRIVA RFP analysis (mounted under /api/assessments) */
const ASSESSMENTS_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/assessments`;
const axAssessments = axios.create({
  baseURL: ASSESSMENTS_BASE,
  withCredentials: true,
});

export async function runRfpPrivaAnalysis(assessmentId) {
  const r = await axAssessments.post(`/${assessmentId}/run-rfp-priva-analysis`);
  return r.data;
}

export async function previewRfpConversion(assessmentId, overrides) {
  const r = await axAssessments.post(
    `/${assessmentId}/preview-rfp-conversion`,
    { overrides: overrides || {} }
  );
  return r.data;
}

export async function convertRfpToSmartEvaluate(assessmentId, overrides, notes) {
  const r = await axAssessments.post(
    `/${assessmentId}/convert-rfp-to-smartevaluate`,
    { overrides: overrides || {}, notes: notes || "" }
  );
  return r.data;
}

export async function uploadRfpCustomerResponses(assessmentId, file) {
  const form = new FormData();
  form.append("file", file);
  const r = await axAssessments.post(
    `/${assessmentId}/upload-customer-responses`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return r.data;
}

export async function clearRfpCustomerResponses(assessmentId) {
  const r = await axAssessments.delete(
    `/${assessmentId}/customer-responses`
  );
  return r.data;
}
